import React, { ChangeEvent, FC, useState } from "react";
import {
  Button,
  DatePicker,
  Input,
  IntervalIcon,
  MeetingViewModel,
  Select,
} from "@components";
import {
  FormControlLabel,
  FormLabel,
  InputAdornment,
  Radio,
  RadioGroup,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import {
  IIntervalFormat,
  IIntervalType,
  IntervalFormats,
  Intervals,
  RepeatTypes,
  TFormats,
  TIntervalTypeValue,
  TMeetingSetup,
  TRepeatTypes,
  setClass,
  useViewModel,
  weekDaysShortEng,
  weekDaysShortUncap,
} from "@utils";
import { isMobile } from "react-device-detect";
import { ru } from "date-fns/locale";
import { DateBlockAdapter } from "../../../../dateBlockAdapter";
import { getTypeOfCountingPlural } from "../../../../../utils/constants/pluralForm";
import { Interval } from "./Interval";
import { endOfDay, getISODay } from "date-fns";
import "../../../../../styles/components/intervalBlock.scss";

interface IntervalBlockProp {
  interval: TIntervalTypeValue;
  repeatType: TRepeatTypes;
  meetingSetup: TMeetingSetup;
  minDate: Date;
  isEdit: boolean;
  onUpdateMeeting: (
    property: "intervalType" | "repeatType" | "meetingSetup",
    value: string | TRepeatTypes | TMeetingSetup
  ) => void;
  showError: boolean;
  meetingFormat: TFormats;
  startDate: Date;
}

export const IntervalBlock: FC<IntervalBlockProp> = ({
  interval,
  onUpdateMeeting,
  repeatType,
  meetingSetup,
  minDate,
  isEdit,
  showError,
  meetingFormat,
  startDate,
}) => {
  const meetingViewModel = useViewModel(MeetingViewModel);
  const [value, setValue] = useState<IIntervalType>(
    Intervals.find((searchInterval) => searchInterval.value === interval)
  );

  const [format, setFormat] = useState<IIntervalFormat>(
    IntervalFormats.find(
      (searchFormat) => searchFormat.value === meetingSetup.intervalFormat
    )
  );

  const updateMeetingSetup = (
    property: keyof TMeetingSetup,
    setupValue: TMeetingSetup[keyof TMeetingSetup]
  ) => {
    onUpdateMeeting("meetingSetup", {
      ...meetingViewModel.meetingData.meetingSetup,
      [property]: setupValue,
    });
  };

  const isDaySelected = (i: number) =>
    meetingSetup.daysOfWeek.find((day) => day === weekDaysShortEng[i]);

  const onDayClick = (i: number) => {
    if (isDaySelected(i)) {
      updateMeetingSetup(
        "daysOfWeek",
        meetingSetup.daysOfWeek.filter((day) => day !== weekDaysShortEng[i])
      );
    } else {
      updateMeetingSetup(
        "daysOfWeek",
        meetingSetup.daysOfWeek.concat(weekDaysShortEng[i])
      );
    }
  };

  const onSelectIntervalType = (newInterval: IIntervalType) => {
    onUpdateMeeting("intervalType", newInterval.value);
    setValue(newInterval);
  };

  const onChangeIntervalType = (newIntervalType: string) =>
    updateMeetingSetup(
      "interval",
      Math.abs(parseInt(newIntervalType, 10)) || 0
    );
  const onChangeIntervalFormat = (newFormat: IIntervalFormat) => {
    updateMeetingSetup("intervalFormat", newFormat.value);
    setFormat(newFormat);
  };
  const onChangeRepeatType = (
    _e: ChangeEvent<HTMLInputElement>,
    type: string
  ) => onUpdateMeeting("repeatType", type);

  const isValidDate = (date: Date) => !Number.isNaN(date.getTime());

  const onExpDateChange = (newDate: Date) => {
    updateMeetingSetup(
      "expDate",
      isValidDate(endOfDay(newDate)) ? endOfDay(newDate) : meetingSetup.expDate
    );
  };

  const getDayOfStartDate = (startDate: Date) => getISODay(startDate) - 1;

  const expDateValue =
    meetingSetup.expDate && isValidDate(meetingSetup.expDate)
      ? new Date(meetingSetup.expDate)
      : endOfDay(new Date());

  return (
    <div className="area intervals">
      <IntervalIcon divClassName="interval-icon" />
      <div>
        {isEdit ? (
          <Select
            classNameRootWrapper={setClass(
              "interval-select",
              isMobile && "mobile"
            )}
            options={Intervals}
            value={value}
            getOptionId={(option: IIntervalType) => option.value}
            onChange={onSelectIntervalType}
            placeholder={"Выберите интервал"}
            renderOption={(item: IIntervalType, isSelected: boolean) => (
              <Interval intervalName={item.name} isSelected={isSelected} />
            )}
            commonContent
          />
        ) : (
          <div
            className={setClass(
              "view-meeting-interval-container",
              isMobile && "mobile"
            )}
          >
            <Input
              variant="square"
              classes="light-disabled"
              value={value.name}
              disabled
            />
          </div>
        )}

        {interval === "ANOTHER" && isEdit && (
          <div className="custom-meeting-area">
            <div
              className={setClass("interval-selection", isMobile && "mobile")}
            >
              Повторять с интервалом
              <Input
                classes="interval-input"
                variant="rounded"
                value={meetingSetup.interval.toString()}
                onChange={onChangeIntervalType}
              />
              <Select
                classNameRootWrapper={setClass(
                  "select-repeat-interval",
                  isMobile && "mobile"
                )}
                options={IntervalFormats}
                value={format}
                getOptionId={(option: IIntervalFormat) => option.value}
                onChange={onChangeIntervalFormat}
                placeholder={""}
                renderOption={(item, isSelected) => (
                  <div
                    className={setClass(
                      "repeat-interval",
                      isSelected && "selected"
                    )}
                  >
                    {getTypeOfCountingPlural(meetingSetup.interval, item.forms)}
                  </div>
                )}
                commonContent
              />
            </div>
            {meetingSetup.intervalFormat === "WEEK" && (
              <div className="repeat-days-block">
                <div>Дни повторения *</div>
                <div
                  className={setClass(
                    "repeat-days-buttons",
                    isMobile && "mobile"
                  )}
                >
                  {[...Array(7).keys()].map((i) => (
                    <Button
                      className={setClass(
                        !isDaySelected(i) && "unselected-day",
                        showError &&
                          meetingSetup.daysOfWeek.length === 0 &&
                          "error-day",
                        getDayOfStartDate(startDate) === i && "underlined-day"
                      )}
                      key={i}
                      title={weekDaysShortUncap[i]}
                      onClick={() => onDayClick(i)}
                    />
                  ))}
                </div>
                {showError && meetingSetup.daysOfWeek.length === 0 && (
                  <div className="error-text">Укажите дни повторения</div>
                )}
              </div>
            )}
          </div>
        )}
        {interval !== "WITHOUT_REPEAT" && isEdit && (
          <RadioGroup
            className="repeat-type-group"
            onChange={onChangeRepeatType}
          >
            <FormLabel className="repeat-type-header">Окончание</FormLabel>
            {meetingFormat === "ONLINE" && (
              <FormControlLabel
                className="repeat-type-label label-never"
                value={RepeatTypes.NEVER}
                checked={repeatType === RepeatTypes.NEVER}
                control={<Radio />}
                label="Никогда"
              />
            )}
            <div className="area end-circle-date">
              <FormControlLabel
                className="repeat-type-label"
                value={RepeatTypes.DATE}
                checked={repeatType === RepeatTypes.DATE}
                control={<Radio />}
                label="Дата"
              />
              <LocalizationProvider
                adapterLocale={ru}
                dateAdapter={DateBlockAdapter}
              >
                <DatePicker
                  value={expDateValue}
                  disabled={repeatType !== RepeatTypes.DATE}
                  disablePast
                  classNameInput="date-picker-edit-meeting"
                  minDate={minDate}
                  onChange={onExpDateChange}
                  inputFormat="dd          MMM"
                  error={
                    showError &&
                    expDateValue.getTime() < minDate.getTime() &&
                    isEdit
                  }
                  shiftPopup={isMobile}
                />
              </LocalizationProvider>
            </div>
            {showError &&
              expDateValue.getTime() < minDate.getTime() &&
              isEdit && (
                <div className="error-text">
                  Дата окончания цикличной встречи не может быть меньше даты
                  окончания встречи.
                </div>
              )}
            <div className="area end-circle-amount">
              <FormControlLabel
                className="repeat-type-label"
                value={RepeatTypes.NUMBER}
                checked={repeatType === RepeatTypes.NUMBER}
                control={<Radio />}
                label="После"
              />
              <TextField
                className="repeat-type-input field"
                type="number"
                value={meetingSetup.numberOfRepeat}
                disabled={repeatType !== RepeatTypes.NUMBER}
                onChange={(e) =>
                  updateMeetingSetup(
                    "numberOfRepeat",
                    Math.abs(parseInt(e.target.value, 10))
                  )
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {meetingSetup.numberOfRepeat % 10 === 1
                        ? "повтора"
                        : "повторов"}
                    </InputAdornment>
                  ),
                }}
              />
            </div>
          </RadioGroup>
        )}
      </div>
    </div>
  );
};
